import React, { useState, useEffect } from 'react';
import { Mail, Phone, MapPin, Calendar, User, FileText, ArrowRight, ChevronDown, ChevronUp, Clock, MessageCircle, Copy, Link as LinkIcon, X, Info } from 'lucide-react';
import { Miembro, Lumisial, InvitationLog } from '../lib/supabase';
import { useMiembroFichaActions } from '../hooks/useMiembroFichaActions';
import { ProvinciaEclesiastica, provinciaEclesiasticaService } from '../services/provinciaEclesiasticaService';
import { lumisialService } from '../services/lumisialService';
import { trasladoMiembroService } from '../services/trasladoMiembroService';
import { historialIngresosService, PeriodoIngreso, formatearAntiguedad } from '../services/historialIngresosService';
import { miembroService } from '../services/miembroService';
import { HistorialTrasladosModal } from './HistorialTrasladosModal';
import { MiembroFotoLightbox } from './MiembroFotoLightbox';
import { DangerConfirmDialog } from './ui/DangerConfirmDialog';
import { ConfirmDialog } from './ui/ConfirmDialog';
import { Badge } from './ui/Badge';
import { deriveEstadoRegistro, ESTADO_REGISTRO_LABEL, ESTADO_REGISTRO_BADGE_VARIANT } from '../utils/estadoRegistro';
import { DialogShell } from './ui/DialogShell';
import { useNombrePais } from '../hooks/useNombrePais';
import { useToast } from './ui/Toast';
import { useAuth } from '../context/AuthContext';
import { calcularEdad, tipoMiembroPorEdad } from '../utils/edad';
import { buildWhatsAppUrl } from '../utils/invitacionValidation';
import { ZONA_OPERACION } from '../utils/dateHelpers';
import { formatDate as formatFechaCivil } from '../utils/formatters';
import { fotoPendienteVigente } from '../utils/compromisoFoto';

function getInvBtnLabel(token: string | null | undefined): string {
  return token ? 'Regenerar link de invitación' : 'Generar link de invitación';
}

function getLogStatus(log: InvitationLog): string {
  if (log.used_at) return 'Usado';
  if (new Date(log.expires_at) < new Date()) return 'Vencido';
  return 'Activo';
}

const currencyFormatter = new Intl.NumberFormat('es-CL', { style: 'currency', currency: 'CLP' });
function formatCurrency(amount: number): string {
  return currencyFormatter.format(amount);
}

function formatDate(date: string): string {
  return new Date(date).toLocaleDateString('es-CL', { day: '2-digit', month: '2-digit', year: 'numeric' });
}

/**
 * Para plazos, no para fechas de calendario. `formatDate` muestra un instante con el reloj de quien
 * mira (created_at, updated_at); las columnas `date` van por `formatFechaCivil`. Ninguna de las dos
 * sirve para una fecha que el servidor decide en Chile.
 */
function formatFechaDePlazo(date: string): string {
  return new Date(date).toLocaleDateString('es-CL', {
    timeZone: ZONA_OPERACION, day: '2-digit', month: '2-digit', year: 'numeric',
  });
}

function formatDateTime(date: string): string {
  const d = new Date(date);
  const fecha = d.toLocaleDateString('es-CL', { day: '2-digit', month: '2-digit', year: 'numeric' });
  const hora = d.toLocaleTimeString('es-CL', { hour: '2-digit', minute: '2-digit' });
  return `${fecha} a las ${hora}`;
}

interface RelatedData {
  provincia: ProvinciaEclesiastica | null;
  lumisial: Lumisial | null;
  count: number;
  periodos: PeriodoIngreso[];
  logs: InvitationLog[];
}

async function loadMiembroRelatedData(
  miembroId: string,
  provinciaId: string | null | undefined,
  lumisialId: string | null | undefined,
): Promise<RelatedData> {
  const [provincia, lumisial, count, periodos, logs] = await Promise.all([
    provinciaId ? provinciaEclesiasticaService.obtenerPorId(provinciaId) : Promise.resolve(null),
    lumisialId ? lumisialService.getById(lumisialId) : Promise.resolve(null),
    trasladoMiembroService.contarTrasladosPorMiembro(miembroId),
    historialIngresosService.obtenerHistorialIngresos(miembroId),
    miembroService.getInvitationLogs(miembroId),
  ]);
  return { provincia, lumisial, count, periodos, logs };
}

const LOG_STATUS_DISPLAY: Record<string, React.ReactNode> = {
  Usado:   <span className="text-green-600">Usado</span>,
  Vencido: <span className="text-red-500">Vencido</span>,
  Activo:  <span className="text-blue-600">Activo</span>,
};

function buildWhatsAppMessage(miembro: Miembro, link: string, obs?: string): string {
  return obs
    ? `Hola ${miembro.nombres}, hemos revisado tu ficha:\n${obs}\n\nLink para corregir (válido 7 días):\n${link}`
    : `Hola ${miembro.nombres}, te invitamos a completar tu ficha:\n${link}\n(válido por 7 días)`;
}

function buildRecordatorioFotoMessage(miembro: Miembro, link: string): string {
  return `Hola ${miembro.nombres}, recibimos tu ficha. Solo falta tu fotografía:\n${link}`;
}

type WhatsAppMessageBuilder = (miembro: Miembro, link: string, obs?: string) => string;

function enviarWhatsApp(
  miembro: Miembro,
  link: string,
  obs?: string,
  buildMessage: WhatsAppMessageBuilder = buildWhatsAppMessage,
): void {
  const msg = buildMessage(miembro, link, obs);
  // `noopener` evita que la página abierta pueda redirigir esta pestaña vía `window.opener`.
  window.open(buildWhatsAppUrl(miembro.telefono ?? '', msg), '_blank', 'noopener,noreferrer');
}

interface FichaAutoregistroSectionProps {
  readonly miembro: Miembro;
  readonly invitationLogs: InvitationLog[];
  readonly onClose: () => void;
  readonly onChanged?: () => void;
}

/**
 * El link nuevo no llega solo: enviarlo es un gesto manual por WhatsApp. Sin este aviso, el único
 * rastro de que hay algo pendiente era un toast que se desvanece.
 */
const AvisoEnviarLinkTrasDevolver: React.FC<{ readonly tieneLink: boolean }> = ({ tieneLink }) => (
  <div className="flex items-start gap-3 rounded-xl border border-blue-200 bg-blue-50 p-3">
    <Info className="w-5 h-5 text-blue-600 shrink-0 mt-0.5" aria-hidden="true" />
    <p className="text-sm text-blue-900">
      {tieneLink
        ? <>Devolviste la ficha y se generó un link nuevo. <span className="font-semibold">El miembro no se entera solo</span>: envíaselo para que pueda corregirla.</>
        : <>Devolviste la ficha, pero <span className="font-semibold">no se pudo generar el link nuevo</span>. Genéralo y envíaselo para que pueda corregirla.</>}
    </p>
  </div>
);

function FichaAutoregistroSection({ miembro, invitationLogs, onClose, onChanged }: FichaAutoregistroSectionProps) {
  const { showToast } = useToast();
  const [historialExpanded, setHistorialExpanded] = useState(false);
  const {
    generandoLink, linkGenerado, linkExpiresAt,
    showConfirmRegistrar, setShowConfirmRegistrar,
    showDevolver, setShowDevolver,
    observacion, setObservacion,
    devolviendo,
    fichaDevuelta,
    generarLink, handleRegistrar, handleDevolver,
  } = useMiembroFichaActions(miembro, onClose, onChanged);
  const [showRegenerarConfirm, setShowRegenerarConfirm] = useState(false);
  const [ahora, setAhora] = useState(() => Date.now());

  useEffect(() => {
    const interval = globalThis.setInterval(() => setAhora(Date.now()), 30_000);
    return () => globalThis.clearInterval(interval);
  }, []);

  const fotoPendiente = fotoPendienteVigente(miembro, ahora);
  const linkFoto = fotoPendiente
    ? `${globalThis.location.origin}/registro/${miembro.invitation_token}`
    : null;

  const hasActiveLink = !!miembro.invitation_token
    && !miembro.invitation_used_at
    && !!miembro.invitation_expires_at
    && new Date(miembro.invitation_expires_at).getTime() > ahora;
  const activeLink = hasActiveLink ? `${globalThis.location.origin}/registro/${miembro.invitation_token}` : null;
  const displayLink = linkGenerado ?? activeLink;
  const displayExpiry = linkExpiresAt ?? (hasActiveLink ? miembro.invitation_expires_at : null);
  // Hay link vigente si lo dice el registro persistido o si acabamos de generar uno en esta sesión.
  const hayLinkVigente = hasActiveLink || !!linkGenerado;
  const handleGenerarClick = () => { if (hayLinkVigente) setShowRegenerarConfirm(true); else generarLink(); };
  const generarBtnLabel = hayLinkVigente ? 'Generar link nuevo' : getInvBtnLabel(miembro.invitation_token);
  // Tras devolver, el prop queda stale: la ficha volvió a "sin_completar" (esperando al miembro).
  const estadoFicha = fichaDevuelta ? 'sin_completar' : miembro.estado_ficha;
  const estadoRegistro = fichaDevuelta ? 'solicitado' : deriveEstadoRegistro(miembro);

  return (
    <>
      <div>
        <Badge variant={ESTADO_REGISTRO_BADGE_VARIANT[estadoRegistro]}>{ESTADO_REGISTRO_LABEL[estadoRegistro]}</Badge>
      </div>

      {miembro.comentario_devolucion_ficha && (
        <div className="rounded-xl border border-amber-200 bg-amber-50 p-3 text-sm text-amber-800">
          <p className="font-medium mb-1">Comentario:</p>
          <p>{miembro.comentario_devolucion_ficha}</p>
        </div>
      )}

      {/* `fichaDevuelta` gana sobre el prop: devolver la ficha anula el compromiso y reemplaza el
          token, pero `miembro` conserva el estado anterior hasta que se recargue. Sin esta condición
          el bloque seguiría ofreciendo el link viejo, que ya responde 404, junto al nuevo. */}
      {fotoPendiente && linkFoto && !fichaDevuelta && (
        <div className="rounded-xl border border-amber-200 bg-amber-50 p-3 space-y-2">
          <p className="text-sm font-medium text-amber-800">
            Falta su fotografía — plazo hasta el {formatFechaDePlazo(miembro.foto_compromiso_hasta!)}
          </p>
          <div className="flex flex-wrap gap-2">
            <button type="button"
              onClick={() => { enviarWhatsApp(miembro, linkFoto, undefined, buildRecordatorioFotoMessage); }}
              className="flex items-center gap-1.5 px-3 py-2 bg-green-50 text-green-700 border border-green-200 rounded-xl text-sm">
              <MessageCircle className="w-4 h-4" /> Recordar por WhatsApp
            </button>
            <button type="button"
              onClick={() => { navigator.clipboard.writeText(linkFoto); showToast('Link copiado', 'success'); }}
              className="flex items-center gap-1.5 px-3 py-2 border border-slate-200 text-slate-600 rounded-xl text-sm">
              <LinkIcon className="w-4 h-4" /> Copiar link
            </button>
            <button type="button"
              onClick={() => { navigator.clipboard.writeText(buildRecordatorioFotoMessage(miembro, linkFoto)); showToast('Mensaje copiado', 'success'); }}
              className="flex items-center gap-1.5 px-3 py-2 border border-slate-200 text-slate-600 rounded-xl text-sm">
              <Copy className="w-4 h-4" /> Copiar mensaje
            </button>
          </div>
        </div>
      )}

      {estadoFicha === 'prerregistrado' && (
        <div className="flex gap-2">
          <button type="button" onClick={() => setShowConfirmRegistrar(true)}
            className="px-4 py-2 bg-green-600 text-white rounded-xl text-sm font-medium hover:bg-green-700">
            Registrar miembro
          </button>
          <button type="button" onClick={() => setShowDevolver(true)}
            className="px-4 py-2 border border-slate-200 text-slate-700 rounded-xl text-sm font-medium hover:bg-slate-50">
            Devolver con comentario
          </button>
        </div>
      )}

      {(estadoFicha === 'sin_completar' || estadoFicha === 'registrado') && (
        <div className="space-y-3">
          {fichaDevuelta && <AvisoEnviarLinkTrasDevolver tieneLink={!!displayLink} />}
          {displayLink && (
            <div className="rounded-xl border border-slate-200 bg-slate-50 p-3 space-y-2">
              <p className="text-sm font-medium text-slate-700">
                Link activo{displayExpiry ? ` — vence el ${formatDateTime(displayExpiry)}` : ''}
              </p>
              <div className="flex flex-wrap gap-2">
                <button type="button" onClick={() => enviarWhatsApp(miembro, displayLink)}
                  className="flex items-center gap-1.5 px-3 py-2 bg-green-50 text-green-700 border border-green-200 rounded-xl text-sm">
                  <MessageCircle className="w-4 h-4" /> Enviar por WhatsApp
                </button>
                <button type="button" onClick={() => { navigator.clipboard.writeText(displayLink); showToast('Link copiado', 'success'); }}
                  className="flex items-center gap-1.5 px-3 py-2 border border-slate-200 text-slate-600 rounded-xl text-sm">
                  <LinkIcon className="w-4 h-4" /> Copiar link
                </button>
                <button type="button" onClick={() => { navigator.clipboard.writeText(buildWhatsAppMessage(miembro, displayLink)); showToast('Mensaje copiado', 'success'); }}
                  className="flex items-center gap-1.5 px-3 py-2 border border-slate-200 text-slate-600 rounded-xl text-sm">
                  <Copy className="w-4 h-4" /> Copiar mensaje
                </button>
              </div>
            </div>
          )}
          <div className="group relative inline-block">
            <button type="button" onClick={handleGenerarClick} disabled={generandoLink}
              className={`px-4 py-2 rounded-xl text-sm font-medium disabled:opacity-50 ${
                hasActiveLink
                  ? 'border border-slate-200 text-slate-600 hover:bg-slate-50'
                  : 'bg-blue-600 text-white hover:bg-blue-700'
              }`}>
              {generandoLink ? 'Generando...' : generarBtnLabel}
            </button>
            {hasActiveLink && !generandoLink && (
              <span className="pointer-events-none absolute bottom-full left-1/2 -translate-x-1/2 mb-2 px-2 py-1 bg-slate-800 text-white text-xs rounded opacity-0 group-hover:opacity-100 transition-opacity whitespace-nowrap">
                Reemplaza el link activo
              </span>
            )}
          </div>
        </div>
      )}

      {showDevolver && (
        <div className="rounded-xl border border-orange-200 bg-orange-50 p-4">
          <p className="text-sm font-medium text-orange-800 mb-2">Comentario para el miembro</p>
          <textarea value={observacion} onChange={e => setObservacion(e.target.value)} maxLength={500} rows={3}
            aria-label="Comentario para el miembro"
            className="w-full rounded-lg border border-orange-200 bg-white px-3 py-2 text-sm resize-none"
            placeholder="Describe qué debe corregir..." />
          <div className="flex gap-2 mt-2">
            <button type="button" onClick={handleDevolver} disabled={devolviendo}
              className="px-3 py-1.5 bg-orange-600 text-white rounded-lg text-sm font-medium disabled:opacity-50">
              {devolviendo ? 'Devolviendo...' : 'Devolver ficha'}
            </button>
            <button type="button" onClick={() => { setShowDevolver(false); setObservacion(''); }} disabled={devolviendo}
              className="px-3 py-1.5 border border-slate-200 text-slate-600 rounded-lg text-sm disabled:opacity-50">
              Cancelar
            </button>
          </div>
          {linkGenerado && (
            <div className="mt-3 flex gap-2">
              <button type="button" onClick={() => enviarWhatsApp(miembro, linkGenerado, observacion)}
                className="flex items-center gap-1.5 px-3 py-2 bg-green-50 text-green-700 border border-green-200 rounded-xl text-sm">
                <MessageCircle className="w-4 h-4" /> Enviar comentario por WhatsApp
              </button>
            </div>
          )}
        </div>
      )}

      {invitationLogs.length > 0 && (
        <div className="border border-slate-200 rounded-xl overflow-hidden">
          <button type="button" onClick={() => setHistorialExpanded(e => !e)}
            className="w-full flex items-center justify-between px-4 py-3 bg-slate-50 text-sm font-medium text-slate-700">
            Historial de invitaciones
            {historialExpanded ? <ChevronUp className="w-4 h-4" /> : <ChevronDown className="w-4 h-4" />}
          </button>
          {!historialExpanded && (
            <div className="px-4 py-2 text-sm text-slate-500">
              Último link: {new Date(invitationLogs[0].generated_at).toLocaleDateString('es-CL', { day: '2-digit', month: 'short' })} —{' '}
              {getLogStatus(invitationLogs[0])}
            </div>
          )}
          {historialExpanded && (
            <table className="w-full text-sm">
              <thead className="bg-slate-50 text-xs text-slate-500">
                <tr>
                  <th className="px-4 py-2 text-left">Fecha</th>
                  <th className="px-4 py-2 text-left">Generado por</th>
                  <th className="px-4 py-2 text-left">Estado</th>
                </tr>
              </thead>
              <tbody>
                {invitationLogs.map(log => (
                  <tr key={log.id} className="border-t border-slate-100">
                    <td className="px-4 py-2">{new Date(log.generated_at).toLocaleDateString('es-CL', { day: '2-digit', month: 'short', year: '2-digit' })}</td>
                    <td className="px-4 py-2">{log.generador?.nombres} {log.generador?.apellidos}</td>
                    <td className="px-4 py-2">
                      {LOG_STATUS_DISPLAY[getLogStatus(log)]}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>
      )}

      <DangerConfirmDialog
        isOpen={showConfirmRegistrar}
        onClose={() => setShowConfirmRegistrar(false)}
        onConfirm={handleRegistrar}
        title="Confirmar registro del miembro"
        message="¿Confirmas que revisaste todos los datos y son correctos? Se sugiere revisar los datos presencialmente con la persona antes de confirmar. Esta acción es irreversible."
        warningItems={['El registro es irreversible', 'Se sugiere revisar los datos presencialmente']}
        checkboxLabel="Confirmo que revisé todos los datos y son correctos"
        confirmText="Registrar miembro"
        cancelText="Cancelar"
      />

      <ConfirmDialog
        isOpen={showRegenerarConfirm}
        onClose={() => setShowRegenerarConfirm(false)}
        onConfirm={() => generarLink()}
        title="Ya hay un link activo"
        message="Este miembro tiene un link de invitación vigente. Generar uno nuevo lo reemplazará y el link anterior dejará de funcionar."
        confirmText="Generar link nuevo"
        cancelText="Cancelar"
        type="archive"
      />
    </>
  );
}

interface MiembroFichaModalProps {
  isOpen: boolean;
  onClose: () => void;
  miembro: Miembro | null;
  onChanged?: () => void;
}

export const MiembroFichaModal: React.FC<MiembroFichaModalProps> = ({
  isOpen,
  onClose,
  miembro,
  onChanged
}) => {
  const [prevMiembro, setPrevMiembro] = useState<Miembro | null>(null);
  const [loadedData, setLoadedData] = useState<RelatedData | null>(null);
  const [showHistorialModal, setShowHistorialModal] = useState(false);
  const [showFotoAmpliada, setShowFotoAmpliada] = useState(false);
  const [mostrarPeriodos, setMostrarPeriodos] = useState(false);
  const { nombre: nombrePaisIngreso, loading: loadingPais } = useNombrePais(miembro?.pais_ingreso);
  const { usuario } = useAuth();
  const esAdmin = usuario?.rol?.nombre?.includes('admin') ?? false;

  if (miembro !== prevMiembro) {
    setPrevMiembro(miembro);
    setLoadedData(null);
  }

  useEffect(() => {
    if (!isOpen || !miembro) return;
    loadMiembroRelatedData(miembro.id, miembro.provincia_eclesiastica_ingreso_id, miembro.lumisial_ingreso_id)
      .then(data => setLoadedData(data))
      .catch((err: unknown) => console.error('Error loading related data:', err));
  }, [isOpen, miembro]);


  if (!isOpen || !miembro) return null;

  const loading = !loadedData;
  const provinciaIngreso = loadedData?.provincia ?? null;
  const lumisialIngreso = loadedData?.lumisial ?? null;
  const cantidadTraslados = loadedData?.count ?? 0;
  const periodosIngreso = loadedData?.periodos ?? [];
  const invitationLogs = loadedData?.logs ?? [];

  const paisIngresoDisplay = loadingPais ? 'Cargando...' : (nombrePaisIngreso || miembro.pais_ingreso);

  const header = (
    <div className="sticky top-0 z-10 bg-gradient-to-r from-blue-600 to-blue-700 text-white p-6 flex items-center justify-between rounded-t-2xl">
      <div className="flex items-center gap-4 min-w-0">
        {miembro.foto_url ? (
          <button
            type="button"
            onClick={() => setShowFotoAmpliada(true)}
            className="flex-shrink-0 rounded-full cursor-zoom-in focus:outline-none focus:ring-2 focus:ring-white/70"
            aria-label="Ampliar foto del miembro"
          >
            <img
              src={miembro.foto_url}
              alt=""
              className="w-14 h-14 rounded-full object-cover border-2 border-white/30"
            />
          </button>
        ) : (
          <div className="w-14 h-14 rounded-full bg-white/20 flex items-center justify-center text-white text-xl font-semibold flex-shrink-0">
            {miembro.nombres?.[0]}{miembro.apellidos?.[0]}
          </div>
        )}
        <div className="min-w-0">
          <h2 className="text-2xl font-bold truncate">{miembro.nombres} {miembro.apellidos}</h2>
          <p className="text-blue-100 text-sm mt-1">Ficha de Miembro</p>
        </div>
      </div>
      <button
        type="button"
        onClick={onClose}
        className="p-2 text-white/80 hover:text-white hover:bg-blue-600 rounded-lg transition-colors flex-shrink-0"
        aria-label="Cerrar"
      >
        <X className="w-6 h-6" />
      </button>
    </div>
  );

  return (
    <DialogShell
      isOpen={isOpen}
      onClose={onClose}
      size="lg"
      header={header}
    >
      <div className="space-y-6">
        <FichaAutoregistroSection miembro={miembro} invitationLogs={invitationLogs} onClose={onClose} onChanged={onChanged} />

        <div className="bg-slate-50 rounded-lg p-5 space-y-4">
          <h3 className="text-lg font-bold text-slate-800 flex items-center gap-2">
            <User className="w-5 h-5" />
            Identidad
          </h3>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <div className="bg-white border border-slate-200 rounded-lg p-4">
              <span className="text-xs font-semibold text-slate-500 uppercase">Edad</span>
              <p className="text-lg font-bold text-slate-800 mt-1">
                {(() => {
                  const edad = calcularEdad(miembro.fecha_nacimiento);
                  if (edad === null) return '—';
                  const tipo = tipoMiembroPorEdad(miembro.fecha_nacimiento) === 'adulto' ? 'Adulto' : 'Niño';
                  return `${tipo} (${edad} ${edad === 1 ? 'año' : 'años'})`;
                })()}
              </p>
            </div>

            {miembro.rut_pasaporte && (
              <div className="bg-white border border-slate-200 rounded-lg p-4">
                <span className="text-xs font-semibold text-slate-500 uppercase">RUT / Pasaporte</span>
                <p className="text-lg font-bold text-slate-800 mt-1">{miembro.rut_pasaporte}</p>
              </div>
            )}

            {miembro.fecha_nacimiento && (
              <div className="bg-white border border-slate-200 rounded-lg p-4">
                <span className="text-xs font-semibold text-slate-500 uppercase flex items-center gap-2">
                  <Calendar className="w-4 h-4" />
                  Fecha Nacimiento
                </span>
                <p className="text-lg font-bold text-slate-800 mt-1">{formatFechaCivil(miembro.fecha_nacimiento)}</p>
              </div>
            )}
          </div>

          <div className="pt-2">
            <h4 className="text-sm font-semibold text-slate-600 uppercase tracking-wide mb-3">Ingreso</h4>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              {miembro.fecha_ingreso && (
                <div>
                  <span className="text-xs font-semibold text-slate-500 uppercase block">Fecha de Ingreso</span>
                  <span className="text-sm text-slate-800">{formatFechaCivil(miembro.fecha_ingreso)}</span>
                </div>
              )}
              {(provinciaIngreso || miembro.pais_ingreso) && (
                <div>
                  <span className="text-xs font-semibold text-slate-500 uppercase block">País de Ingreso</span>
                  <span className="text-sm text-slate-800">
                    {loading ? 'Cargando...' : provinciaIngreso?.nombre || paisIngresoDisplay}
                  </span>
                </div>
              )}
              {(miembro.ciudad_ingreso || (lumisialIngreso?.ciudad && !miembro.ciudad_ingreso)) && (
                <div>
                  <span className="text-xs font-semibold text-slate-500 uppercase block">Ciudad de Ingreso</span>
                  <span className="text-sm text-slate-800">
                    {loading ? 'Cargando...' : miembro.ciudad_ingreso || lumisialIngreso?.ciudad}
                  </span>
                </div>
              )}
              {(lumisialIngreso || miembro.lumisial_ingreso) && (
                <div>
                  <span className="text-xs font-semibold text-slate-500 uppercase block">Lumisial de Ingreso</span>
                  <span className="text-sm text-slate-800">
                    {loading ? 'Cargando...' : lumisialIngreso?.nombre || miembro.lumisial_ingreso}
                  </span>
                </div>
              )}
            </div>
          </div>
        </div>

        <div className="bg-slate-50 rounded-lg p-5 space-y-4">
          <h3 className="text-lg font-bold text-slate-800 flex items-center gap-2">
            <Mail className="w-5 h-5" />
            Contacto
          </h3>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {miembro.email && (
              <div className="flex items-start gap-3">
                <Mail className="w-5 h-5 text-slate-400 mt-0.5" />
                <div>
                  <span className="text-xs font-semibold text-slate-500 uppercase block">Email</span>
                  <span className="text-sm text-slate-800">{miembro.email}</span>
                </div>
              </div>
            )}
            {miembro.telefono && (
              <div className="flex items-start gap-3">
                <Phone className="w-5 h-5 text-slate-400 mt-0.5" />
                <div>
                  <span className="text-xs font-semibold text-slate-500 uppercase block">Teléfono</span>
                  <span className="text-sm text-slate-800">{miembro.telefono}</span>
                </div>
              </div>
            )}
            {miembro.direccion && (
              <div className="flex items-start gap-3">
                <MapPin className="w-5 h-5 text-slate-400 mt-0.5" />
                <div>
                  <span className="text-xs font-semibold text-slate-500 uppercase block">Dirección</span>
                  <span className="text-sm text-slate-800">{miembro.direccion}</span>
                </div>
              </div>
            )}
            {miembro.comuna && (
              <div>
                <span className="text-xs font-semibold text-slate-500 uppercase block">Comuna</span>
                <span className="text-sm text-slate-800">{miembro.comuna}</span>
              </div>
            )}
            {miembro.ciudad && (
              <div>
                <span className="text-xs font-semibold text-slate-500 uppercase block">Ciudad</span>
                <span className="text-sm text-slate-800">{miembro.ciudad}</span>
              </div>
            )}
          </div>
        </div>

        <div className="bg-slate-50 rounded-lg p-5 space-y-4">
          <h3 className="text-lg font-bold text-slate-800">Perfil y aptitudes</h3>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {miembro.actividad_profesion && (
              <div>
                <span className="text-xs font-semibold text-slate-500 uppercase block">Actividad / Profesión</span>
                <span className="text-sm text-slate-800">{miembro.actividad_profesion}</span>
              </div>
            )}
            {miembro.habilidad_talento && (
              <div>
                <span className="text-xs font-semibold text-slate-500 uppercase block">Habilidad / Talento</span>
                <span className="text-sm text-slate-800">{miembro.habilidad_talento}</span>
              </div>
            )}
          </div>
        </div>

        <div className="bg-slate-50 rounded-lg p-5 space-y-4">
          <h3 className="text-lg font-bold text-slate-800">Contacto de emergencia</h3>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {miembro.nombre_contacto_emergencia && (
              <div>
                <span className="text-xs font-semibold text-slate-500 uppercase block">Nombre</span>
                <span className="text-sm text-slate-800">{miembro.nombre_contacto_emergencia}</span>
              </div>
            )}
            {miembro.numero_contacto_emergencia && (
              <div>
                <span className="text-xs font-semibold text-slate-500 uppercase block">Teléfono</span>
                <span className="text-sm text-slate-800">{miembro.numero_contacto_emergencia}</span>
              </div>
            )}
          </div>
        </div>

        {miembro.tipo_miembro === 'adulto' && (
          <div className="bg-emerald-50 rounded-lg p-5 space-y-4">
            <h3 className="text-lg font-bold text-slate-800">Compromiso económico</h3>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <div>
                <span className="text-xs font-semibold text-emerald-600 uppercase block">Aporte Lumisial</span>
                <span className="text-xl font-bold text-slate-800">{formatCurrency(miembro.aporte_mensual)}</span>
              </div>
              <div>
                <span className="text-xs font-semibold text-emerald-600 uppercase block">Cuota Iglesia</span>
                <span className="text-xl font-bold text-slate-800">{formatCurrency(miembro.aporte_cuota_iglesia)}</span>
              </div>
              {miembro.modo_pago_cuota_iglesia && (
                <div>
                  <span className="text-xs font-semibold text-emerald-600 uppercase block">Modo de Pago</span>
                  <span className="text-sm text-slate-800">{miembro.modo_pago_cuota_iglesia}</span>
                </div>
              )}
            </div>
          </div>
        )}

        <div className="text-xs text-slate-500 text-center pt-4 border-t border-slate-200">
          Registro creado: {formatDate(miembro.created_at)}
          {miembro.updated_at && miembro.updated_at !== miembro.created_at && (
            <> • Última actualización: {formatDate(miembro.updated_at)}</>
          )}
        </div>

        {esAdmin && (
          <div className="space-y-6">
            <div className="flex items-center gap-3 pt-2" aria-hidden="true">
              <div className="h-px flex-1 bg-slate-200" />
              <span className="text-xs font-semibold text-slate-400 uppercase tracking-wider">Junta Sacerdotal</span>
              <div className="h-px flex-1 bg-slate-200" />
            </div>

            {miembro.observaciones_miembro && (
              <div className="bg-slate-50 rounded-lg p-5 space-y-3">
                <h3 className="text-lg font-bold text-slate-800 flex items-center gap-2">
                  <FileText className="w-5 h-5" />
                  Observaciones
                </h3>
                <p className="text-sm text-slate-700 whitespace-pre-wrap leading-relaxed">{miembro.observaciones_miembro}</p>
              </div>
            )}

            <div className="bg-slate-50 border border-slate-200 rounded-lg overflow-hidden">
              <div className="p-4">
                <div className="flex items-center justify-between mb-3">
                  <div>
                    <p className="text-xs font-medium text-slate-500 uppercase flex items-center gap-2">
                      <Clock className="w-4 h-4" />
                      Histórico de Ingresos
                    </p>
                    {periodosIngreso.length > 0 && (
                      <p className="text-sm text-slate-700 mt-1 font-semibold">
                        {miembro.numero_ingreso === 1 && 'Primer ingreso'}
                        {miembro.numero_ingreso === 2 && 'Segundo ingreso'}
                        {miembro.numero_ingreso === 3 && 'Tercer ingreso'}
                        {miembro.numero_ingreso > 3 && `${miembro.numero_ingreso}° ingreso`}
                      </p>
                    )}
                  </div>
                  {periodosIngreso.length > 0 && (() => {
                    const periodoActivo = periodosIngreso.find(p => p.esActivo);
                    return (
                      <div className="text-right">
                        {periodoActivo && (
                          <div>
                            <p className="text-xs text-slate-500">Antigüedad actual</p>
                            <p className="text-sm font-bold text-blue-600">
                              {formatearAntiguedad(periodoActivo.duracion.años, periodoActivo.duracion.meses)}
                            </p>
                          </div>
                        )}
                      </div>
                    );
                  })()}
                </div>

                {periodosIngreso.length > 0 && (
                  <button
                    onClick={() => { setMostrarPeriodos(!mostrarPeriodos); }}
                    className="flex items-center gap-2 text-sm text-blue-600 hover:text-blue-700 font-medium transition-colors"
                  >
                    {mostrarPeriodos ? (
                      <>
                        <ChevronUp className="w-4 h-4" />
                        Ocultar detalle de períodos
                      </>
                    ) : (
                      <>
                        <ChevronDown className="w-4 h-4" />
                        Ver detalle de períodos ({periodosIngreso.length})
                      </>
                    )}
                  </button>
                )}
              </div>

              {mostrarPeriodos && periodosIngreso.length > 0 && (
                <div className="border-t border-slate-200 bg-white">
                  <div className="divide-y divide-slate-100">
                    {periodosIngreso.map((periodo) => (
                      <div
                        key={periodo.numeroIngreso}
                        className={`p-4 ${
                          periodo.esActivo
                            ? 'bg-blue-50 border-l-4 border-l-blue-500'
                            : 'hover:bg-slate-50'
                        } transition-colors`}
                      >
                        <div className="flex items-start justify-between">
                          <div className="flex-1">
                            <div className="flex items-center gap-2 mb-2">
                              <span className={`text-sm font-bold ${
                                periodo.esActivo ? 'text-blue-700' : 'text-slate-700'
                              }`}>
                                {periodo.numeroIngreso === 1 && 'Primer Ingreso'}
                                {periodo.numeroIngreso === 2 && 'Segundo Ingreso'}
                                {periodo.numeroIngreso === 3 && 'Tercer Ingreso'}
                                {periodo.numeroIngreso > 3 && `${periodo.numeroIngreso}° Ingreso`}
                              </span>
                              {periodo.esActivo && (
                                <span className="px-2 py-0.5 bg-blue-600 text-white text-xs font-semibold rounded-full">
                                  Activo
                                </span>
                              )}
                            </div>
                            <div className="space-y-1">
                              <p className="text-xs text-slate-600">
                                <span className="font-medium">Inicio:</span>{' '}
                                {formatDate(periodo.fechaInicio)}
                              </p>
                              {periodo.fechaFin && (
                                <p className="text-xs text-slate-600">
                                  <span className="font-medium">Fin:</span>{' '}
                                  {formatDate(periodo.fechaFin)}
                                </p>
                              )}
                            </div>
                          </div>
                          <div className="text-right ml-4">
                            <p className="text-xs text-slate-500 mb-1">Duración</p>
                            <p className={`text-sm font-bold ${
                              periodo.esActivo ? 'text-blue-700' : 'text-slate-700'
                            }`}>
                              {formatearAntiguedad(periodo.duracion.años, periodo.duracion.meses)}
                            </p>
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {periodosIngreso.length === 0 && !loading && (
                <div className="p-4 text-center text-sm text-slate-500">
                  No hay registros de historial de ingresos
                </div>
              )}

              {loading && (
                <div className="p-4 text-center text-sm text-slate-500">
                  Cargando historial...
                </div>
              )}
            </div>

            <button
              type="button"
              onClick={() => setShowHistorialModal(true)}
              className="flex items-center gap-2 text-sm text-blue-600 hover:text-blue-700 font-medium transition-colors"
            >
              <ArrowRight className="w-4 h-4" />
              Ver historial de traslados
              {cantidadTraslados > 0 && (
                <span className="inline-flex items-center justify-center px-2 py-0.5 rounded-full text-xs font-bold bg-blue-100 text-blue-700">
                  {cantidadTraslados}
                </span>
              )}
            </button>
          </div>
        )}
      </div>

      <MiembroFotoLightbox
        isOpen={showFotoAmpliada}
        onClose={() => setShowFotoAmpliada(false)}
        fotoUrl={miembro.foto_url ?? ''}
        nombreCompleto={`${miembro.nombres} ${miembro.apellidos}`}
      />

      <HistorialTrasladosModal
        isOpen={showHistorialModal}
        onClose={() => setShowHistorialModal(false)}
        miembroId={miembro.id}
        miembroNombre={`${miembro.nombres} ${miembro.apellidos}`}
      />
    </DialogShell>
  );
};
